/**
 * Salesforce Enhanced Chat API Configuration Service
 * Manages environment-specific Salesforce settings and feature flags
 */

// Salesforce Environment Configuration
const SALESFORCE_CONFIG = {
  // Salesforce Messaging API Configuration
  API_BASE_URL: process.env.REACT_APP_SALESFORCE_MESSAGING_URL || 'https://intouch-messaging.my.salesforce.com',
  
  // Organization and deployment IDs
  ORGANIZATION_ID: process.env.REACT_APP_SALESFORCE_ORG_ID || '',
  DEVELOPER_NAME: process.env.REACT_APP_SALESFORCE_DEVELOPER_NAME || 'InTouchSupport',
  
  // Salesforce API Configuration
  CAPABILITIES_VERSION: process.env.REACT_APP_SALESFORCE_CAPABILITIES_VERSION || '1',
  PLATFORM: process.env.REACT_APP_SALESFORCE_PLATFORM || 'Web',
  SALESFORCE_VERSION: process.env.REACT_APP_SALESFORCE_VERSION || '1.0.0',
  
  // EventSource Configuration
  EVENT_SOURCE_RECONNECT_TIMEOUT: parseInt(process.env.REACT_APP_SALESFORCE_RECONNECT_TIMEOUT) || 3000,
  EVENT_SOURCE_MAX_RETRIES: parseInt(process.env.REACT_APP_SALESFORCE_MAX_RETRIES) || 5,
  
  // JWT Configuration
  JWT_STORAGE_KEY: 'salesforce_jwt_token',
  JWT_REFRESH_THRESHOLD: parseInt(process.env.REACT_APP_SALESFORCE_JWT_REFRESH_THRESHOLD) || 300000, // 5 minutes
  
  // Conversation Configuration
  DEFAULT_ROUTING_ATTRIBUTES: {
    'Routing_Department__c': process.env.REACT_APP_SALESFORCE_DEFAULT_DEPARTMENT || 'Support',
    'Routing_Product__c': process.env.REACT_APP_SALESFORCE_DEFAULT_PRODUCT || 'Ignition',
    'Routing_Priority__c': process.env.REACT_APP_SALESFORCE_DEFAULT_PRIORITY || 'Medium'
  },
  
  // Feature Flags
  ENABLE_NEW_SUPPORT_CHAT: process.env.REACT_APP_ENABLE_NEW_SUPPORT_CHAT === 'true',
  ENABLE_SALESFORCE_DEBUG: process.env.REACT_APP_ENABLE_SALESFORCE_DEBUG === 'true',
  ENABLE_SALESFORCE_TELEMETRY: process.env.REACT_APP_ENABLE_SALESFORCE_TELEMETRY === 'true',
  
  // Timeouts and Retry Configuration
  API_TIMEOUT: parseInt(process.env.REACT_APP_SALESFORCE_API_TIMEOUT) || 30000,
  CONNECTION_TIMEOUT: parseInt(process.env.REACT_APP_SALESFORCE_CONNECTION_TIMEOUT) || 10000,
  MESSAGE_RETRY_ATTEMPTS: parseInt(process.env.REACT_APP_SALESFORCE_MESSAGE_RETRY_ATTEMPTS) || 3,
  
  // Development/Testing Configuration
  MOCK_MODE: process.env.REACT_APP_SALESFORCE_MOCK_MODE === 'true',
  TEST_CONVERSATION_ID: process.env.REACT_APP_SALESFORCE_TEST_CONVERSATION_ID || null
};

/**
 * Get Salesforce configuration for the current environment
 */
export const getSalesforceConfig = () => {
  // Validate required configuration
  const requiredFields = ['API_BASE_URL', 'ORGANIZATION_ID', 'DEVELOPER_NAME'];
  const missingFields = requiredFields.filter(field => !SALESFORCE_CONFIG[field]);
  
  if (missingFields.length > 0 && !SALESFORCE_CONFIG.MOCK_MODE) {
    console.warn('Missing required Salesforce configuration fields:', missingFields);
  }
  
  return SALESFORCE_CONFIG;
};

/**
 * Get API endpoints for Salesforce Enhanced Chat API
 */
export const getSalesforceEndpoints = () => {
  const baseUrl = SALESFORCE_CONFIG.API_BASE_URL;
  
  return {
    // Authentication endpoints - Salesforce v2 API with Cognito JWT
    AUTHENTICATED_TOKEN_URL: `${baseUrl}/iamessage/api/v2/authorization/authenticated/access-token`,
    CONTINUATION_TOKEN_URL: `${baseUrl}/iamessage/api/v2/authorization/continuation-access-token`,
    
    // Conversation endpoints
    CONVERSATION_URL: `${baseUrl}/iamessage/api/v2/conversation`,
    CONVERSATION_EVENTS_URL: `${baseUrl}/iamessage/api/v2/conversation/events`,
    CONVERSATION_MESSAGE_URL: (conversationId) => `${baseUrl}/iamessage/api/v2/conversation/${conversationId}/message`,
    TYPING_INDICATOR_URL: (conversationId) => `${baseUrl}/iamessage/api/v2/conversation/${conversationId}/typing`
  };
};

/**
 * Get routing attributes with user context for authenticated conversations
 * @param {Object} userContext - Required user information
 * @param {string} userContext.userId - User ID from authId
 * @param {string} userContext.email - User email address
 * @param {string} userContext.name - User display name
 * @param {Object} customAttributes - Additional routing attributes
 */
export const getRoutingAttributes = (userContext = {}, customAttributes = {}) => {
  const { userId, email, name } = userContext;
  
  // Validate required user context
  if (!userId) {
    console.warn('[Salesforce:Config] User ID is required for authenticated conversations');
  }
  
  return {
    ...SALESFORCE_CONFIG.DEFAULT_ROUTING_ATTRIBUTES,
    'Routing_User_ID__c': userId || '',
    'ZNR_EndUserEmail_c': email || '',
    'ZNR_EndUserName_c': name || '',
    'Routing_Auth_Type__c': 'AUTHENTICATED',
    ...customAttributes
  };
};

/**
 * Get developer name (esDeveloperName) for API requests
 * This should match the Embedded Service Deployment Developer Name
 */
export const getEsDeveloperName = () => {
  return SALESFORCE_CONFIG.DEVELOPER_NAME;
};

/**
 * Get JWT configuration
 */
export const getJwtConfig = () => {
  return {
    storageKey: SALESFORCE_CONFIG.JWT_STORAGE_KEY,
    refreshThreshold: SALESFORCE_CONFIG.JWT_REFRESH_THRESHOLD
  };
};

/**
 * Get EventSource configuration
 */
export const getEventSourceConfig = () => {
  return {
    reconnectTimeout: SALESFORCE_CONFIG.EVENT_SOURCE_RECONNECT_TIMEOUT,
    maxRetries: SALESFORCE_CONFIG.EVENT_SOURCE_MAX_RETRIES
  };
};

/**
 * Check if Salesforce is enabled and properly configured
 */
export const isSalesforceEnabled = () => {
  return SALESFORCE_CONFIG.ENABLE_NEW_SUPPORT_CHAT && 
         (SALESFORCE_CONFIG.MOCK_MODE || (SALESFORCE_CONFIG.API_BASE_URL && SALESFORCE_CONFIG.ORGANIZATION_ID));
};

/**
 * Check if Salesforce debug mode is enabled
 */
export const isSalesforceDebugEnabled = () => {
  return SALESFORCE_CONFIG.ENABLE_SALESFORCE_DEBUG;
};

/**
 * Get Salesforce request headers
 */
export const getSalesforceHeaders = (jwt = null) => {
  const headers = {
    'Content-Type': 'application/json',
    'X-Salesforce-Version': SALESFORCE_CONFIG.SALESFORCE_VERSION,
    'X-Platform': SALESFORCE_CONFIG.PLATFORM
  };
  
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  
  return headers;
};

/**
 * Get environment-specific configuration summary for debugging
 */
export const getSalesforceConfigSummary = () => {
  return {
    environment: process.env.NODE_ENV,
    apiBaseUrl: SALESFORCE_CONFIG.API_BASE_URL,
    organizationId: SALESFORCE_CONFIG.ORGANIZATION_ID ? '***configured***' : 'missing',
    developerName: SALESFORCE_CONFIG.DEVELOPER_NAME,
    isEnabled: isSalesforceEnabled(),
    isMockMode: SALESFORCE_CONFIG.MOCK_MODE,
    isDebugEnabled: isSalesforceDebugEnabled(),
    capabilitiesVersion: SALESFORCE_CONFIG.CAPABILITIES_VERSION,
    platform: SALESFORCE_CONFIG.PLATFORM
  };
};

// Export the configuration object for direct access if needed
export default SALESFORCE_CONFIG;